/**
 * @hook useStaffPreview
 * @description The staff-only "view as" tool. Deliberately a separate file
 * from useAuth's persona switcher, because the two look similar on screen and
 * are not the same thing at all:
 *
 *   Persona switch (useAuth)   Your own identity, a different hat. Anyone may
 *                              do it, within what their account holds.
 *   Staff preview (here)       Renders the platform as somebody who is NOT
 *                              signed in. Staff only.
 *   Impersonation (elsewhere)  Becomes a DIFFERENT PERSON, with their data on
 *                              screen. Approval-gated, audited, and nothing
 *                              in this file should be mistaken for it.
 *
 * ── Why guest is a preview and not a persona ─────────────────────────
 *
 * Guest is the absence of an account. Nobody signed in "has" it, so it cannot
 * appear in a hat switcher without the switcher lying. What an operator
 * actually wants is to see the signed-out experience without losing their
 * session — check that the pricing page reads right to a stranger, that the
 * landing CTA says "Get started" and not "Go to dashboard" — and then get
 * straight back to work. That is a rendering mode, not an authentication
 * change.
 *
 * ── What it does and does not simulate ───────────────────────────────
 *
 * IT DOES: make the app report `isAuthenticated: false` and withhold the
 * user object, so every public surface takes its signed-out branch. That is
 * one seam, which is why the disguise is consistent rather than patched in
 * per component.
 *
 * IT DOES NOT: revoke the session token. Requests still carry it, so a public
 * page that fetches a personalised endpoint will still get personalised data.
 * This is a preview of the EXPERIENCE, not a security sandbox — and it is not
 * a way to check what a guest is authorised to read. For that, use a signed-
 * out browser, which is the only honest test of an unauthenticated session.
 *
 * State lives in sessionStorage, not localStorage: a preview that survived a
 * browser restart would greet an operator as an anonymous visitor the next
 * morning with no memory of having switched, and the natural diagnosis would
 * be "I have been logged out".
 */
import { useCallback, useMemo, useState } from 'react';
import { PERSONAS } from '@moksy/common/src/permissions/personas.js';

export const GUEST_PREVIEW_KEY = 'moksy_guest_preview';

const readFlag = () => {
  try { return sessionStorage.getItem(GUEST_PREVIEW_KEY) === '1'; } catch { return false; }
};

const STAFF_GROUP = 'owner';

export const useStaffPreview = (user) => {
  const [active, setActive] = useState(readFlag);

  // Group rather than a role list: `group === 'owner'` is the one staff
  // signal the users-service maintains as an invariant (it is derived from
  // role on every write), so it cannot drift the way a copied role array can.
  const canPreview = useMemo(
    () => String(user?.group || '').toLowerCase() === STAFF_GROUP,
    [user?.group]
  );

  /**
   * Enter guest preview.
   *
   * Navigates to the public home with a full page load rather than a router
   * push. Two reasons: the operator is almost certainly standing on an
   * authenticated route, and the instant `isAuthenticated` flips to false
   * ProtectedRoute would bounce them to /login — which looks exactly like
   * being logged out, the impression this tool must never give. A hard
   * navigation lands them on the guest home first, so the flag only takes
   * effect somewhere it makes sense.
   */
  const start = useCallback(() => {
    if (!canPreview) return false;
    try { sessionStorage.setItem(GUEST_PREVIEW_KEY, '1'); } catch { return false; }
    setActive(true);
    window.location.assign('/');
    return true;
  }, [canPreview]);

  /** Leave preview and return to the operator console. */
  const end = useCallback((returnTo = '/admin/console') => {
    try { sessionStorage.removeItem(GUEST_PREVIEW_KEY); } catch { /* noop */ }
    setActive(false);
    window.location.assign(returnTo);
  }, []);

  // A non-staff account can never be previewing, even if the flag was planted
  // by hand: the flag is a rendering hint, and the entitlement is re-checked
  // here rather than trusted from storage.
  const isPreviewing = active && canPreview;

  return {
    isPreviewing,
    canPreview,
    previewPersona: isPreviewing ? PERSONAS.GUEST : null,
    startGuestPreview: start,
    endPreview: end
  };
};

export default useStaffPreview;
